import type {
  MimirElectronContextBridgeObject,
} from '@mimir/common/desktop/ipc';
import type {
  VfsPortReadRequest,
  VfsPortRequest,
  VfsPortResponse,
  VfsPortWriteRequest,
} from '@mimir/common/types/ipcVfs';
import { VFS_PORT_WINDOW_MESSAGE_TYPE } from '@mimir/common/types/ipcVfs';
import { reconstructVfsPortError } from '@mimir/common/util/vfsPortError';

// MessagePort backed read and write to bypass cloning and serialisation between electrons
// node context and our application.
// The Uint8Array has to be a SharedArrayBuffer backed one to enable zero-copy transfer.
export interface VfsPortClient {
  read(openFileId: number, buf: Uint8Array<SharedArrayBuffer>, fileOffset: number): Promise<number>;
  write(openFileId: number, buf: Uint8Array<SharedArrayBuffer>, fileOffset: number): Promise<number>;
}

// The MessagePort satisfies this specialisation.
// But this is more mockable than an entire MessagePort.
export interface VfsPortLike {
  addEventListener(type: 'message', listener: (event: { data: VfsPortResponse }) => void): void;
  addEventListener(type: 'close', listener: () => void): void;
  start(): void;
  postMessage(request: VfsPortRequest): void;
}

interface PendingRequest {
  resolve: (bytesTransferred: number) => void;
  reject: (error: Error) => void;
}

interface VfsPortWindowMessage {
  type: typeof VFS_PORT_WINDOW_MESSAGE_TYPE;
}

function isVfsPortWindowMessage(data: unknown): data is VfsPortWindowMessage {
  return (
    typeof data === 'object'
    && data !== null
    && (data as { type?: unknown }).type === VFS_PORT_WINDOW_MESSAGE_TYPE
  );
}

export function createVfsPortClient(port: VfsPortLike): VfsPortClient {
  const pending = new Map<number, PendingRequest>();
  let nextRequestId = 1;
  let closed = false;

  function rejectAll(reason: Error): void {
    closed = true;
    for (const entry of pending.values()) {
      entry.reject(reason);
    }
    pending.clear();
  }

  port.addEventListener('message', (event) => {
    const response = event.data;
    const entry = pending.get(response.requestId);
    if (entry === undefined) {
      return;
    }
    pending.delete(response.requestId);
    if (response.ok) {
      entry.resolve(response.bytesTransferred);
      return;
    }
    entry.reject(reconstructVfsPortError(response.error));
  });
  // MessagePort in Electron's renderer main world has a 'close' event when the
  // remote end (or its owning WebContents) goes away.
  port.addEventListener('close', () => {
    rejectAll(new Error('VFS MessagePort was closed'));
  });
  port.start();

  function send(request: VfsPortRequest): Promise<number> {
    if (closed) {
      return Promise.reject(new Error('VFS MessagePort was closed'));
    }
    return new Promise<number>((resolve, reject) => {
      pending.set(request.requestId, { resolve, reject });
      // Structured clone preserves the SharedArrayBuffer backing of request.buf,
      // giving the main process a live view over the same shared memory.
      port.postMessage(request);
    });
  }

  return {
    read(openFileId: number, buf: Uint8Array<SharedArrayBuffer>, fileOffset: number): Promise<number> {
      const request: VfsPortReadRequest = {
        requestId: nextRequestId++,
        op: 'read',
        openFileId,
        buf,
        fileOffset,
      };
      return send(request);
    },
    write(openFileId: number, buf: Uint8Array<SharedArrayBuffer>, fileOffset: number): Promise<number> {
      const request: VfsPortWriteRequest = {
        requestId: nextRequestId++,
        op: 'write',
        openFileId,
        buf,
        fileOffset,
      };
      return send(request);
    },
  };
}

// Requests the VFS MessagePort from the preload context and resolves once it
// has been delivered via window.postMessage. Returns null when the exposed
// bridge does not support the port protocol (older Mimir Desktop builds),
// signalling the wrapper to stay on the legacy invoke-based path.
export function initVfsPortClient(
  mimirElectron: MimirElectronContextBridgeObject,
): Promise<VfsPortClient | null> {
  const postVfsPort = mimirElectron.postVfsPort;
  if (postVfsPort === undefined) {
    return Promise.resolve(null);
  }
  return new Promise<VfsPortClient | null>((resolve, reject) => {
    function onMessage(event: MessageEvent): void {
      if (!isVfsPortWindowMessage(event.data)) {
        return;
      }
      const port = event.ports[0];
      if (port === undefined) {
        return;
      }
      window.removeEventListener('message', onMessage);
      resolve(createVfsPortClient(port));
    }
    window.addEventListener('message', onMessage);
    postVfsPort().catch((ex: unknown) => {
      window.removeEventListener('message', onMessage);
      reject(ex instanceof Error ? ex : new Error(String(ex)));
    });
  });
}
